import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  type EdgeMouseHandler,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "@/features/network-topology/network-flow.css";
import { useMemo } from "react";
import { DeviceNode } from "@/features/network-topology/DeviceNode";
import { childIdFromEdgeId, layoutNetworkAsFlow } from "@/features/network-topology/layout";
import { useNetworkActions, useNetworkTopology } from "@/features/network-topology/useNetworkTopology";

/**
 * React Flow + dagre による工場ネットワーク可視化。
 *
 * - カスタム DeviceNode で機器表現
 * - クリック選択を Zustand と同期（sourceOfTruth: store）
 * - 編集はパネル側のボタンから行うため、グラフ上は drag/connect 無効化
 */

const NODE_TYPES = { deviceNode: DeviceNode } as const;

function NetworkGraphInner() {
  const { nodes: networkNodes, selectedNodeId, selectedEdgeChildId } = useNetworkTopology();
  const { selectNode, selectEdge } = useNetworkActions();

  const { nodes, edges } = useMemo(() => {
    const flow = layoutNetworkAsFlow(networkNodes, { selectedEdgeChildId });
    return {
      nodes: flow.nodes.map<Node>((n) => ({ ...n, selected: n.id === selectedNodeId })),
      edges: flow.edges as Edge[],
    };
  }, [networkNodes, selectedNodeId, selectedEdgeChildId]);

  const handleNodeClick: NodeMouseHandler = (_event, node) => {
    selectNode(node.id);
  };
  const handleEdgeClick: EdgeMouseHandler = (_event, edge) => {
    selectEdge(childIdFromEdgeId(edge.id));
  };

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={NODE_TYPES}
      onNodeClick={handleNodeClick}
      onEdgeClick={handleEdgeClick}
      onPaneClick={() => {
        selectNode(null);
        selectEdge(null);
      }}
      fitView
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
    >
      <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      <Controls showInteractive={false} />
      <MiniMap pannable zoomable />
    </ReactFlow>
  );
}

export function NetworkGraph() {
  return (
    <ReactFlowProvider>
      <NetworkGraphInner />
    </ReactFlowProvider>
  );
}
